import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { generateSampleInvoice } from '../utils/invoiceGenerator';
import { uploadInvoiceToStorage } from '../utils/firebaseStorage';
import { getAuth } from 'firebase/auth';
import { getFirestore, doc, getDoc, updateDoc, Timestamp, collection, getDocs } from 'firebase/firestore';
// date-fns imports removed - not used in this component

interface InvoiceItem {
  id: number;
  description: string;
  quantity: number;
  price: number;
}

interface Invoice {
  invoiceNumber: string;
  issueDate: Date;
  dueDate: Date;
  customerName: string;
  customerCompany: string;
  customerEmail: string;
  customerPhone: string;
  customerAddress: string;
  items: InvoiceItem[];
  taxRate: number;
  notes: string;
  documentType: string;
  documentTitle: string;
  status?: string;
  language?: string;
  cardId?: string; // 추가: 카드 ID
  subtotal?: number; // 추가: 소계
  taxAmount?: number; // 추가: 세금
  totalAmount?: number; // 추가: 총계
}

interface BusinessProfile {
  businessName: string;
  address?: string;
  phone?: string;
  email?: string;
  website?: string;
  abn?: string;
  bankName?: string;
  bankAccountNumber?: string;
  bankAccountHolder?: string;
  logoUrl?: string;
}

const InvoiceEditPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { invoiceId } = useParams();
  const userId = getAuth().currentUser?.uid;

  // 상태 관리
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [businessProfile, setBusinessProfile] = useState<BusinessProfile | null>(null);
  const [editedInvoice, setEditedInvoice] = useState<Invoice | null>(null);
  const [invoiceLang, setInvoiceLang] = useState(i18n.language);

  // 안전한 번역 함수
  const safeT = (key: string, options: any = {}) => {
    try {
      const tOptions = typeof options === 'object' && options !== null ? options : {};
      const current = t(key, tOptions);
      if (typeof current === 'string' && current !== key) return current;

      const en = t(key, { ...tOptions, lng: 'en' });
      if (typeof en === 'string' && en !== key) return en;

      return key;
    } catch (error) {
      console.warn('Translation error for key:', key, error);
      return key;
    }
  };

  // Helper function to format currency
  const formatCurrency = (amount: number) => {
    if (typeof amount !== 'number') {
      const currencySymbol = safeT('common.currencySymbol') || '$';
      return `${currencySymbol}0`;
    }
    
    const currencySymbol = safeT('common.currencySymbol') || '$';
    const currentLanguage = i18n.language;
    
    let formattedAmount;
    if (currentLanguage === 'ko') {
      formattedAmount = amount.toLocaleString('ko-KR');
    } else if (currentLanguage === 'en') {
      formattedAmount = amount.toLocaleString('en-US');
    } else {
      formattedAmount = amount.toLocaleString();
    }
    
    return `${currencySymbol}${formattedAmount}`;
  };

  // 인보이스 및 비즈니스 프로필 fetch
  useEffect(() => {
    let isMounted = true;
    
    console.log('🔍 InvoiceEditPage useEffect - URL params:', { invoiceId, userId });
    
    const fetchData = async () => {
      try {
        setLoading(true);
        setError('');
        
        if (!userId) {
          console.log('❌ No userId, redirecting to login');
          navigate('/login');
          return;
        }
        
        if (!invoiceId) {
          console.log('❌ Missing invoiceId:', { invoiceId });
          setError('인보이스 ID가 누락되었습니다');
          setLoading(false);
          return;
        }
        
        console.log('✅ Starting fetchData with params:', { userId, invoiceId });
        
        const db = getFirestore();
        
        // 먼저 사용자의 모든 카드에서 해당 인보이스를 찾기
        const userRef = doc(db, 'users', userId);
        const cardsRef = collection(userRef, 'cards');
        const cardsSnap = await getDocs(cardsRef);
        
        let foundInvoice = null;
        let foundCardId = null;
        
        for (const cardDoc of cardsSnap.docs) {
          const cardId = cardDoc.id;
          const invoiceRef = doc(db, 'users', userId, 'cards', cardId, 'invoices', invoiceId);
          const invoiceSnap = await getDoc(invoiceRef);
          
          if (invoiceSnap.exists()) {
            foundInvoice = invoiceSnap.data();
            foundCardId = cardId;
            console.log('🔍 Invoice found in card:', cardId);
            break;
          }
        }
        
        if (!foundInvoice || !foundCardId) {
          throw new Error(`Invoice not found: ${invoiceId}`);
        }
        
        console.log('🔍 Invoice data loaded:', foundInvoice);
        
        // 비즈니스 프로필도 동일한 카드에서 가져오기 (앱과 동일)
        const profileRef = doc(db, 'users', userId, 'cards', foundCardId, 'businessProfile', 'info');
        console.log('🔍 Profile ref path:', profileRef.path);
        
        const profileSnap = await getDoc(profileRef);
        console.log('🔍 Profile exists:', profileSnap.exists());
        
        if (isMounted) {
          // 날짜 안전 변환 함수
          const safeConvertDate = (dateValue: any): Date => {
            if (dateValue instanceof Date) return dateValue;
            if (dateValue && typeof dateValue === 'object' && 'toDate' in dateValue) {
              return dateValue.toDate();
            }
            if (dateValue) return new Date(dateValue);
            return new Date();
          };

          const invoiceWithDefaults = {
            ...foundInvoice,
            cardId: foundCardId, // 찾은 카드 ID 추가
            documentType: foundInvoice.documentType || 'invoice',
            documentTitle: foundInvoice.documentTitle || t('invoiceCreation.documentTypeInvoice'),
            notes: foundInvoice.notes === 'Please make payment within 30 days of the issuance date.' 
              ? t('invoiceCreation.defaultNotes')
              : foundInvoice.notes,
            issueDate: safeConvertDate(foundInvoice.issueDate),
            dueDate: safeConvertDate(foundInvoice.dueDate),
            items: Array.isArray(foundInvoice.items) ? foundInvoice.items : [
              { id: 1, description: '', quantity: 1, price: 0 }
            ]
          } as Invoice;
          
          console.log('✅ Setting invoice data:', invoiceWithDefaults);
          
          setEditedInvoice(invoiceWithDefaults);
          setInvoiceLang(foundInvoice.language || i18n.language);
          if (profileSnap.exists()) {
            setBusinessProfile(profileSnap.data() as BusinessProfile);
          }
        }
      } catch (e) {
        console.error('❌ Fetch error details:', e);
        if (isMounted) {
          setError(e instanceof Error ? e.message : 'Failed to load invoice');
        }
      } finally {
        if (isMounted) {
          console.log('✅ Setting loading to false');
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => { isMounted = false; };
  }, [userId, invoiceId]);

  // 실시간 계산 함수
  const calculateTotals = (items: InvoiceItem[], taxRate: number = 10) => {
    const subtotal = items.reduce((sum, item) => {
      const quantity = parseFloat(String(item.quantity)) || 0;
      const price = parseFloat(String(item.price)) || 0;
      return sum + (quantity * price);
    }, 0);
    
    const taxAmount = (subtotal * taxRate) / 100;
    const totalAmount = subtotal + taxAmount;
    
    return { subtotal, taxAmount, totalAmount };
  };

  // 수량: 앞 0 제거, 1 이상 보장
  const normalizeQuantity = (val: string) => {
    const s = String(val).trim();
    if (s === '') return 1;
    const n = parseInt(s.replace(/^0+(?=[1-9])/, ''), 10);
    return isNaN(n) || n < 1 ? 1 : n;
  };

  // 단가: 숫자·소수점만 허용, 빈 값은 '' 유지 (한글 IME 충돌 방지)
  const parsePriceInput = (val: string): number | string => {
    let s = String(val).replace(/[^0-9.]/g, '');
    if (s === '') return '';
    const parts = s.split('.');
    if (parts.length > 2) s = parts[0] + '.' + parts.slice(1).join('');
    if (s === '.' || s.endsWith('.')) return s;
    const n = parseFloat(s);
    return isNaN(n) || n < 0 ? '' : n;
  };

  // 아이템 업데이트 함수
  const updateItem = (index: number, field: keyof InvoiceItem, value: any) => {
    if (!editedInvoice) return;
    
    let parsedValue: any = value;
    if (field === 'quantity') {
      parsedValue = normalizeQuantity(String(value));
    } else if (field === 'price') {
      parsedValue = parsePriceInput(String(value));
    }

    const updatedItems = [...editedInvoice.items];
    updatedItems[index] = { ...updatedItems[index], [field]: parsedValue };
    
    const { subtotal, taxAmount, totalAmount } = calculateTotals(updatedItems, editedInvoice.taxRate);
    
    setEditedInvoice({
      ...editedInvoice,
      items: updatedItems,
      subtotal,
      taxAmount,
      totalAmount,
    });
  };

  // 고객 정보 업데이트 함수
  const updateCustomerInfo = (field: keyof Invoice, value: any) => {
    if (!editedInvoice) return;
    setEditedInvoice({
      ...editedInvoice,
      [field]: value,
    });
  };

  // 필수 입력 검증
  const validateForm = () => {
    if (!editedInvoice?.customerName || !editedInvoice.customerName.trim()) {
      alert(safeT('invoiceEdit.customerNameRequired', '고객명을 입력해주세요'));
      return false;
    }
    
    if (!editedInvoice?.items || editedInvoice.items.length === 0) {
      alert(safeT('invoiceEdit.atLeastOneItemRequired', '최소 1개의 아이템이 필요합니다'));
      return false;
    }
    
    for (let i = 0; i < editedInvoice.items.length; i++) {
      const item = editedInvoice.items[i];
      if (!item.description || !item.description.trim()) {
        alert(safeT('invoiceEdit.itemDescriptionRequired', `아이템 ${i + 1}의 설명을 입력해주세요`));
        return false;
      }
      if (!item.quantity || parseFloat(String(item.quantity)) <= 0) {
        alert(safeT('invoiceEdit.validQuantityRequired', `아이템 ${i + 1}의 올바른 수량을 입력해주세요`));
        return false;
      }
      if (parseFloat(String(item.price)) < 0) {
        alert(safeT('invoiceEdit.validPriceRequired', `아이템 ${i + 1}의 올바른 가격을 입력해주세요`));
        return false;
      }
    }
    
    return true;
  };

  // 저장 함수
  const handleSave = async () => {
    if (!validateForm()) {
      alert(safeT('invoiceEdit.validationError'));
      return;
    }

    setSaving(true);
    try {
      const sanitizedItems = editedInvoice!.items.map(item => ({
        ...item,
        quantity: parseFloat(String(item.quantity)) || 0,
        price: parseFloat(String(item.price)) || 0,
      }));
      
      const sanitizedInvoice = {
        ...editedInvoice!,
        items: sanitizedItems,
      };

      if (!sanitizedInvoice.items || sanitizedInvoice.items.length === 0) {
        throw new Error('인보이스 아이템이 없습니다. 최소 1개의 아이템이 필요합니다.');
      }

      if (!businessProfile?.businessName) {
        throw new Error('비즈니스 프로필의 회사명이 없습니다. 비즈니스 프로필을 먼저 설정해주세요.');
      }

      if (!sanitizedInvoice.customerName) {
        throw new Error('고객명이 없습니다. 고객명을 입력해주세요.');
      }

      const hasValidItems = sanitizedInvoice.items.some(item => 
        item.description && item.description.trim() && 
        item.quantity > 0 && 
        item.price >= 0
      );

      if (!hasValidItems) {
        throw new Error('유효한 아이템이 없습니다. 아이템 설명, 수량, 가격을 확인해주세요.');
      }

      // PDF 재생성
      const businessProfileForPdf = {
        businessName: businessProfile?.businessName || '',
        address: businessProfile?.address || '',
        phone: businessProfile?.phone || '',
        email: businessProfile?.email || '',
        website: businessProfile?.website || '',
        abn: businessProfile?.abn || '',
        bankName: businessProfile?.bankName || '',
        bankAccountNumber: businessProfile?.bankAccountNumber || '',
        bankAccountHolder: businessProfile?.bankAccountHolder || '',
        logoUrl: businessProfile?.logoUrl || ''
      };
      
      const invoiceDataForPdf = {
        ...sanitizedInvoice,
        issueDate: (() => {
          const date = sanitizedInvoice.issueDate;
          if (date instanceof Date) return date;
          if (date && typeof date === 'object' && 'toDate' in date) return (date as any).toDate();
          if (date) return new Date(date);
          return new Date();
        })(),
        dueDate: (() => {
          const date = sanitizedInvoice.dueDate;
          if (date instanceof Date) return date;
          if (date && typeof date === 'object' && 'toDate' in date) return (date as any).toDate();
          if (date) return new Date(date);
          return new Date();
        })(),
      };

      await i18n.changeLanguage(invoiceLang);
      
      // PDF 생성 (에러 핸들링 강화)
      console.log('🔍 InvoiceEditPage: Generating PDF with language:', invoiceLang || i18n.language);
      let pdfBlob;
      try {
        pdfBlob = await generateSampleInvoice(invoiceDataForPdf, businessProfileForPdf, invoiceLang || i18n.language || 'en');
        
        if (!pdfBlob || pdfBlob.size === 0) {
          throw new Error('PDF 생성 실패: 빈 파일이 생성되었습니다.');
        }
        
        console.log('🔍 InvoiceEditPage: PDF generated successfully, size:', pdfBlob.size);
      } catch (pdfError) {
        console.error('PDF 생성 오류:', pdfError);
        alert('PDF 생성에 실패했습니다. 다시 시도해주세요.');
        setSaving(false);
        return;
      }

      // Firebase Storage에 PDF 업로드 (에러 핸들링 강화)
      let downloadUrl;
      try {
        downloadUrl = await uploadInvoiceToStorage(pdfBlob, userId!, invoiceId!, editedInvoice.cardId!);
        
        if (!downloadUrl) {
          throw new Error('PDF 업로드 실패: 다운로드 URL을 받지 못했습니다.');
        }
        
        console.log('🔍 InvoiceEditPage: PDF uploaded successfully, URL:', downloadUrl);
      } catch (uploadError) {
        console.error('PDF 업로드 오류:', uploadError);
        alert('PDF 업로드에 실패했습니다. 다시 시도해주세요.');
        setSaving(false);
        return;
      }

      // Firestore 업데이트
      const db = getFirestore();
      
      // 앱과 동일한 경로로 업데이트
      let invoiceRef;
      if (editedInvoice?.cardId) {
        // 카드 ID가 있으면 해당 카드의 인보이스 업데이트
        invoiceRef = doc(db, 'users', userId!, 'cards', editedInvoice.cardId, 'invoices', invoiceId!);
      } else {
        // 카드 ID가 없으면 모든 카드에서 찾기
        const cardsRef = collection(db, 'users', userId!, 'cards');
        const cardsSnapshot = await getDocs(cardsRef);
        
        for (const cardDoc of cardsSnapshot.docs) {
          const cardId = cardDoc.id;
          const cardInvoiceRef = doc(db, 'users', userId!, 'cards', cardId, 'invoices', invoiceId!);
          const cardInvoiceSnap = await getDoc(cardInvoiceRef);
          
          if (cardInvoiceSnap.exists()) {
            invoiceRef = cardInvoiceRef;
            break;
          }
        }
        
        if (!invoiceRef) {
          throw new Error('인보이스를 찾을 수 없습니다.');
        }
      }

      const pdfPath = editedInvoice?.cardId 
        ? `users/${userId}/cards/${editedInvoice.cardId}/invoices/${invoiceId}.pdf`
        : `users/${userId}/invoices/${invoiceId}.pdf`;

      await updateDoc(invoiceRef, {
        ...sanitizedInvoice,
        language: invoiceLang,
        pdfUrl: downloadUrl,
        pdfPath: pdfPath,
        pdfFileName: `${invoiceId}.pdf`,
        updatedAt: Timestamp.fromDate(new Date()),
      });

      alert(safeT('invoiceEdit.saveSuccess'));
      navigate('/invoices');

    } catch (error) {
      console.error('Error during save:', error);
      alert(error instanceof Error ? error.message : '저장 중 오류가 발생했습니다.');
    } finally {
      setSaving(false);
    }
  };

  // 아이템 추가 함수
  const addItem = () => {
    if (!editedInvoice) return;
    
    const newItem = {
      id: Date.now(),
      description: '',
      quantity: 1,
      price: 0,
    };
    
    const updatedItems = [...editedInvoice.items, newItem];
    const { subtotal, taxAmount, totalAmount } = calculateTotals(updatedItems, editedInvoice.taxRate);
    
    setEditedInvoice({
      ...editedInvoice,
      items: updatedItems,
      subtotal,
      taxAmount,
      totalAmount,
    });
  };

  // 아이템 삭제 함수
  const removeItem = (index: number) => {
    if (!editedInvoice || !Array.isArray(editedInvoice.items) || editedInvoice.items.length <= 1) return;
    
    const updatedItems = editedInvoice.items.filter((_, i) => i !== index);
    const { subtotal, taxAmount, totalAmount } = calculateTotals(updatedItems, editedInvoice.taxRate);
    
    setEditedInvoice({
      ...editedInvoice,
      items: updatedItems,
      subtotal,
      taxAmount,
      totalAmount,
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">{safeT('invoiceEdit.loading', '인보이스 정보 로딩 중...')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button 
            onClick={() => navigate(-1)}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700"
          >
            {safeT('goBack', '뒤로 가기')}
          </button>
        </div>
      </div>
    );
  }

  if (!editedInvoice) return null;

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              {safeT('invoiceEdit.editInvoice', '인보이스 편집')}
            </h1>
            <p className="text-gray-600">{editedInvoice.invoiceNumber}</p>
          </div>

          {/* Document Type Selection */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {safeT('invoiceCreation.documentTitle', '문서 제목')}
            </h3>
            <select
              value={editedInvoice.documentType || 'invoice'}
              onChange={(e) => {
                const value = e.target.value;
                if (value === 'custom') {
                  setEditedInvoice({ ...editedInvoice, documentType: 'custom', documentTitle: '' });
                } else {
                  const documentTypeKeys: { [key: string]: string } = {
                    'invoice': 'invoiceCreation.documentTypeInvoice',
                    'quotation': 'invoiceCreation.documentTypeQuotation',
                    'taxInvoice': 'invoiceCreation.documentTypeTaxInvoice',
                    'estimate': 'invoiceCreation.documentTypeEstimate',
                    'receipt': 'invoiceCreation.documentTypeReceipt'
                  };
                  
                  const translationKey = documentTypeKeys[value] || 'invoiceCreation.documentTypeInvoice';
                  const translatedTitle = safeT(translationKey);
                  
                  setEditedInvoice({ ...editedInvoice, documentType: value, documentTitle: translatedTitle });
                }
              }}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="invoice">{safeT('invoiceCreation.documentTypeInvoice')}</option>
              <option value="quotation">{safeT('invoiceCreation.documentTypeQuotation')}</option>
              <option value="taxInvoice">{safeT('invoiceCreation.documentTypeTaxInvoice')}</option>
              <option value="estimate">{safeT('invoiceCreation.documentTypeEstimate')}</option>
              <option value="receipt">{safeT('invoiceCreation.documentTypeReceipt')}</option>
              <option value="custom">{safeT('invoiceCreation.documentTypeCustom')}</option>
            </select>
            
            {editedInvoice.documentType === 'custom' && (
              <input
                type="text"
                value={editedInvoice.documentTitle || ''}
                onChange={(e) => setEditedInvoice({ ...editedInvoice, documentTitle: e.target.value })}
                placeholder={safeT('invoiceCreation.documentTitlePlaceholder')}
                className="w-full mt-2 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            )}
          </div>

          {/* Invoice Status */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {safeT('invoiceEdit.invoiceStatus', '인보이스 상태')}
            </h3>
            <select
              value={editedInvoice.status || 'pending'}
              onChange={(e) => setEditedInvoice({ ...editedInvoice, status: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="pending">{safeT('invoiceEdit.statusPending', '결제 대기중')}</option>
              <option value="paid">{safeT('invoiceEdit.statusPaid', '결제 완료')}</option>
              <option value="overdue">{safeT('invoiceEdit.statusOverdue', '결제 기한 초과')}</option>
              <option value="cancelled">{safeT('invoiceEdit.statusCancelled', '취소됨')}</option>
            </select>
            
            <div className="mt-2">
              {editedInvoice.status === 'pending' && (
                <span className="inline-block px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-sm">
                  {safeT('invoiceEdit.statusPendingInfo', '결제 대기 중')}
                </span>
              )}
              {editedInvoice.status === 'paid' && (
                <span className="inline-block px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm">
                  {safeT('invoiceEdit.statusPaidInfo', '결제가 완료되었습니다')}
                </span>
              )}
              {editedInvoice.status === 'overdue' && (
                <span className="inline-block px-3 py-1 bg-red-100 text-red-800 rounded-full text-sm">
                  {safeT('invoiceEdit.statusOverdueInfo', '결제 기한이 지났습니다')}
                </span>
              )}
              {editedInvoice.status === 'cancelled' && (
                <span className="inline-block px-3 py-1 bg-gray-100 text-gray-800 rounded-full text-sm">
                  {safeT('invoiceEdit.statusCancelledInfo', '인보이스가 취소되었습니다')}
                </span>
              )}
            </div>
          </div>

          {/* Customer Information */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {safeT('invoiceEdit.customerInformation')}
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {safeT('invoiceEdit.customerName')} *
                </label>
                <input
                  type="text"
                  value={editedInvoice.customerName || ''}
                  onChange={(e) => updateCustomerInfo('customerName', e.target.value)}
                  placeholder={safeT('invoiceEdit.customerName')}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-100"
                  readOnly
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {safeT('invoiceEdit.customerEmail')}
                </label>
                <input
                  type="email"
                  value={editedInvoice.customerEmail || ''}
                  onChange={(e) => updateCustomerInfo('customerEmail', e.target.value)}
                  placeholder={safeT('invoiceEdit.customerEmail')}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {safeT('invoiceEdit.customerPhone')}
                </label>
                <input
                  type="tel"
                  value={editedInvoice.customerPhone || ''}
                  onChange={(e) => updateCustomerInfo('customerPhone', e.target.value)}
                  placeholder={safeT('invoiceEdit.customerPhone')}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Invoice Items */}
          <div className="mb-8">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {safeT('invoiceEdit.invoiceItems')}
              </h3>
              <button
                onClick={addItem}
                className="flex items-center px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                {safeT('invoiceEdit.addItem')}
              </button>
            </div>
            
            <div className="space-y-4">
              {editedInvoice.items.map((item, index) => (
                <div key={item.id || index} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex justify-between items-center mb-4">
                    <h4 className="font-medium text-gray-900">
                      {safeT('invoiceEdit.itemNumber', { index: index + 1 })}
                    </h4>
                    {editedInvoice.items.length > 1 && (
                      <button
                        onClick={() => removeItem(index)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {safeT('invoiceEdit.itemDescription')}
                      </label>
                      <input
                        type="text"
                        value={item.description}
                        onChange={(e) => updateItem(index, 'description', e.target.value)}
                        placeholder={safeT('invoiceEdit.itemDescription')}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {safeT('invoiceEdit.quantity')}
                      </label>
                      <input
                        type="number"
                        value={item.quantity}
                        onChange={(e) => updateItem(index, 'quantity', e.target.value)}
                        min="1"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {safeT('invoiceEdit.unitPrice')}
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-2 text-gray-500">
                          {safeT('common.currencySymbol')}
                        </span>
                        <input
                          type="text"
                          inputMode="decimal"
                          value={item.price}
                          onChange={(e) => updateItem(index, 'price', e.target.value)}
                          className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-4 text-right">
                    <span className="text-sm text-gray-600">
                      {safeT('invoiceEdit.amount')}: 
                    </span>
                    <span className="ml-2 font-semibold text-blue-600">
                      {formatCurrency((parseFloat(String(item.quantity)) || 0) * (parseFloat(String(item.price)) || 0))}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Totals */}
          <div className="mb-8">
            <div className="bg-gray-50 rounded-lg p-6">
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">{safeT('invoiceEdit.subtotal')}</span>
                  <span className="font-medium">{formatCurrency(editedInvoice.subtotal || 0)}</span>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">
                    {safeT('invoiceEdit.tax')} (
                    <input
                      type="number"
                      value={editedInvoice.taxRate || 10}
                      onChange={(e) => {
                        const newTaxRate = parseFloat(e.target.value) || 0;
                        const { subtotal, taxAmount, totalAmount } = calculateTotals(editedInvoice.items, newTaxRate);
                        setEditedInvoice({
                          ...editedInvoice,
                          taxRate: newTaxRate,
                          subtotal,
                          taxAmount,
                          totalAmount,
                        });
                      }}
                      min="0"
                      max="100"
                      className="w-16 px-2 py-1 border border-gray-300 rounded text-center"
                    />
                    %)
                  </span>
                  <span className="font-medium">{formatCurrency(editedInvoice.taxAmount || 0)}</span>
                </div>
                
                <div className="border-t pt-3">
                  <div className="flex justify-between">
                    <span className="text-lg font-semibold text-gray-900">
                      {safeT('invoiceEdit.total')}
                    </span>
                    <span className="text-lg font-semibold text-blue-600">
                      {formatCurrency(editedInvoice.totalAmount || 0)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Notes */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {safeT('invoiceEdit.notes')}
            </h3>
            <textarea
              value={editedInvoice.notes || ''}
              onChange={(e) => updateCustomerInfo('notes', e.target.value)}
              placeholder={safeT('invoiceEdit.notesPlaceholder')}
              rows={4}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Save Button */}
          <div className="text-center">
            <button
              onClick={handleSave}
              disabled={saving}
              className="bg-black text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-gray-800 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {saving ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  {safeT('invoiceEdit.saving')}
                </div>
              ) : (
                safeT('invoiceEdit.saveChanges')
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoiceEditPage;
